/**
 * Driving state for `.split-shell` (app.css) — a resizable, collapsible left rail.
 *
 * The CSS and this file are one unit: the shell reads `--rail-w` and the `collapsed`
 * / `dragging` classes, and `narrowPx` here must match the `@media (max-width: …)`
 * beside `.split-shell` — change one, change the other.
 *
 * Each page passes its OWN localStorage keys. Rail widths are per-page: the columns
 * hold different content, so one width for both is wrong.
 * ⚠️ Any new key must also be added to `KEEP_ON_LOGOUT` in `stores.ts`, or the rail
 * geometry is wiped on logout — silently.
 */
export interface SplitRailOpts {
  /** localStorage key for the rail width in px. */
  keyW: string;
  /** localStorage key for the collapsed flag ('1' / '0'). */
  keyCollapsed: string;
  /** The `.split-shell` element. A getter, not a value — the component owns the
   *  `bind:this` and this module only ever reads it. */
  getShell: () => HTMLElement | undefined;
  min?: number;
  max?: number;
  /** Floor for the main pane. Mirrors the `minmax(min(480px, 100%), 1fr)` track. */
  mainMin?: number;
  dividerW?: number;
  /** Below this width the shell is one column and the assistant lives in the tab bar. */
  narrowPx?: number;
}

export function createSplitRail(opts: SplitRailOpts) {
  const MIN = opts.min ?? 280;
  const MAX = opts.max ?? 620;
  const MAIN_MIN = opts.mainMin ?? 480;
  const DIVIDER_W = opts.dividerW ?? 9;
  const NARROW = opts.narrowPx ?? 1100;

  let w = $state<number>(Math.min(MAX, Math.max(MIN, Number(localStorage.getItem(opts.keyW)) || 380)));
  let collapsed = $state(localStorage.getItem(opts.keyCollapsed) === '1');
  let dragging = $state(false);
  let isNarrow = $state(false);

  /** Widest the rail may be, measured from the SHELL — not the window. `.split-shell`
   *  sits inside `.main`, ~320px narrower (250px sidebar + its padding), so
   *  `window.innerWidth - MAIN_MIN` overstates the room by that much and the
   *  "keep the main pane >= MAIN_MIN" promise fails at every width below ~1440. */
  function railMax(): number {
    const shellW = opts.getShell()?.getBoundingClientRect().width ?? window.innerWidth;
    return Math.max(MIN, Math.min(MAX, shellW - DIVIDER_W - MAIN_MIN));
  }
  const clamp = (px: number) => Math.max(MIN, Math.min(railMax(), px));

  const persist = () => {
    localStorage.setItem(opts.keyW, String(Math.round(w)));
    localStorage.setItem(opts.keyCollapsed, collapsed ? '1' : '0');
  };

  $effect(() => {
    const mq = window.matchMedia(`(max-width: ${NARROW}px)`);
    isNarrow = mq.matches;
    const on = () => (isNarrow = mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  });

  const showTab = $derived(collapsed || isNarrow);

  function toggle() {
    collapsed = !collapsed;
    localStorage.setItem(opts.keyCollapsed, collapsed ? '1' : '0');
  }

  /** Force the rail open. For code paths that PUT something in the rail (a seeded
   *  conversation, a background job's result) — content arriving where the user
   *  cannot see it reads as the action having failed. Callers on a page whose
   *  assistant can also live in the tab bar must check `showTab` afterwards and
   *  switch to that tab, since a narrow viewport keeps the rail hidden anyway. */
  function reveal() {
    if (!collapsed) return;
    collapsed = false;
    localStorage.setItem(opts.keyCollapsed, '0');
  }

  function startDrag(e: PointerEvent) {
    e.preventDefault();
    const shell = opts.getShell();
    if (!shell) return;
    dragging = true;
    const shellLeft = shell.getBoundingClientRect().left;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const onMove = (ev: PointerEvent) => {
      const px = ev.clientX - shellLeft;
      if (px < 180) { collapsed = true; return; }   // drag past the edge -> collapse
      collapsed = false;
      w = clamp(px);
    };
    const onUp = () => {
      dragging = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      persist();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  /** Keyboard equivalent of the drag. The handle must stay a real `<button>` or this
   *  is unreachable, which is the only way a keyboard user can resize the rail. */
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); return; }
    if (e.key === 'ArrowLeft') w = clamp(w - 24);
    else if (e.key === 'ArrowRight') w = clamp(w + 24);
    else return;
    e.preventDefault();
    localStorage.setItem(opts.keyW, String(Math.round(w)));
  }

  return {
    get w() { return w; },
    get collapsed() { return collapsed; },
    get dragging() { return dragging; },
    /** True when the assistant belongs in the page's tab bar rather than the rail. */
    get showTab() { return showTab; },
    toggle,
    reveal,
    startDrag,
    onKey,
  };
}
